// @ts-nocheck
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiTrash2 } from "react-icons/fi";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { ReactComponent as LightningIcon } from "../../assets/icons/lightning.svg";
import ProfilePictureRounded from "../ProfilePictureRounded";

const HEAD_CLASS =
  "border-b border-eai-border bg-eai-surface-hover px-4 py-2.5 text-left font-outfit text-eai-meta font-medium uppercase text-eai-ink-subtle";

const CELL_CLASS =
  "hidden truncate px-4 py-3 font-inter text-eai-body-sm text-eai-ink-subtle md:table-cell";

// Which modal each action opens, per energy-model type — keeps the handlers free of switch blocks
const MODAL_ROUTES = {
  Solar: {
    delete: modalViewsConst.deleteSolarEnergyModel,
    edit: modalViewsConst.newSolarPowerCurve,
    preview: modalViewsConst.solarEnergyModelPreview,
  },
  Wind: {
    delete: modalViewsConst.deletePerformanceCurve,
    edit: modalViewsConst.editPerformanceCurve,
    preview: modalViewsConst.powerCurveGraph,
  },
};

export const PCurveRow = ({ pCurveRecord, modelType, organizations }) => {
  const dispatch = useDispatch();
  const routes = MODAL_ROUTES[modelType];

  const open = (action, properties) => {
    if (!routes?.[action]) return;
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: routes[action],
        properties,
      })
    );
  };

  const orgIds = pCurveRecord.organizationIds;
  const primaryOrg =
    !pCurveRecord.organizationId && orgIds?.length && organizations?.loaded
      ? organizations.data?.[orgIds[0]]
      : null;

  return (
    <tr
      onClick={() => open("preview", pCurveRecord)}
      className="cursor-pointer border-b border-eai-border transition-colors hover:bg-eai-surface-hover"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-eai-surface-hover shadow-eai-subtle">
            {/* lightning.svg sets no stroke of its own, so it needs currentColor forced in */}
            <LightningIcon className="h-5 w-5 text-eai-hero-accent [&_path]:stroke-current" />
          </div>
          <span className="eai-card-title truncate">{pCurveRecord.name}</span>
        </div>
      </td>

      <td className={CELL_CLASS}>{modelType}</td>
      <td className={CELL_CLASS}>{pCurveRecord.xUnit}</td>
      <td className={CELL_CLASS}>{pCurveRecord.yUnit}</td>

      <td className={CELL_CLASS}>
        {primaryOrg && (
          <div className="flex items-center gap-2">
            <div title={primaryOrg.name}>
              <ProfilePictureRounded
                pictureURL={primaryOrg.profilePictureUrl}
                diameter={28}
                className="border border-eai-border"
              />
            </div>
            {orgIds.length > 1 && (
              <span className="font-inter text-eai-body-xs text-eai-ink-subtle">
                +{orgIds.length - 1} more
              </span>
            )}
          </div>
        )}
      </td>

      <td className="px-4 py-3">
        {/* Destructive first, then the Edit pill — the app's standard action row */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            aria-label="Delete energy model"
            title="Delete energy model"
            onClick={(e) => {
              e.stopPropagation();
              open("delete", { ...pCurveRecord, __modalSize: "sm" });
            }}
            className="eai-btn-icon eai-btn-icon-danger"
          >
            <FiTrash2 size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              open("edit", pCurveRecord);
            }}
            className="eai-btn-outline-xs"
          >
            Edit
          </button>
        </div>
      </td>
    </tr>
  );
};

export const PCurveTable = ({ pCurveRecords, solarEnergyRecords }) => {
  const { organizations } = useSelector((state) => state.user);

  const rows = [
    ...pCurveRecords.map((record) => ({ record, modelType: "Wind" })),
    ...solarEnergyRecords.map((record) => ({ record, modelType: "Solar" })),
  ];

  return (
    // One table, so the header can't drift out of alignment with the body it labels
    <div className="h-full overflow-auto">
      <table className="w-full border-separate border-spacing-0 text-left">
        <thead className="sticky top-0 z-10">
          <tr>
            <th className={HEAD_CLASS}>Name</th>
            <th className={`${HEAD_CLASS} hidden md:table-cell`}>Type</th>
            <th className={`${HEAD_CLASS} hidden md:table-cell`}>X Unit</th>
            <th className={`${HEAD_CLASS} hidden md:table-cell`}>Y Unit</th>
            <th className={`${HEAD_CLASS} hidden md:table-cell`}>Organization</th>
            <th className={`${HEAD_CLASS} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ record, modelType }) => (
            <PCurveRow
              key={record.id}
              modelType={modelType}
              pCurveRecord={record}
              organizations={organizations}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
